import { NavigateFunction } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { getFeatureFlag, getAppConfiguration } from '@/services/ecs';
import type { PolicyEvaluateResponse } from '@/models/flw-orchestrator.types';
import { Device } from '@/models/flw-orchestrator.types';
import { warnDev } from '@/logging/devConsole';

/**
 * Navigation context that is passed to navigation functions
 */
export interface NavigationContext {
  selectedDevice?: Device | null;
  mfaResult?: PolicyEvaluateResponse;
  policyParams?: any;
  [key: string]: any;
}

/**
 * Options for navigation behavior
 */
export interface NavigationOptions {
  checkKillSwitch?: boolean;
  checkFeatureFlags?: (keyof ReturnType<typeof getAppConfiguration>['featureFlags'])[];
  state?: NavigationContext;
  replace?: boolean; // Replace current history entry instead of pushing new one
}

/**
 * Navigation service that centralizes all navigation logic with ECS checks
 * This allows applying cross-cutting concerns like kill switch checks before navigating
 */
export class NavigationService {
  /**
   * Generic navigation method with ECS checks
   * @param navigate - React Router navigate function
   * @param route - Route path to navigate to (use ROUTES constant)
   * @param context - Optional navigation context/state
   * @param options - Optional navigation options (ECS checks, replace history, etc.)
   * @returns boolean indicating if navigation was allowed
   */
  static navigate(
    navigate: NavigateFunction,
    route: string,
    context?: NavigationContext,
    options?: NavigationOptions
  ): boolean {
    if (!this.canNavigate(options)) {
      warnDev(`Navigation to ${route} blocked by ECS checks`);
      return false;
    }

    const navigateOptions: { state?: NavigationContext; replace?: boolean } = {};
    if (context) {
      navigateOptions.state = context;
    }
    if (options?.replace) {
      navigateOptions.replace = true;
    }

    if (Object.keys(navigateOptions).length > 0) {
      navigate(route, navigateOptions);
    } else {
      navigate(route);
    }
    return true;
  }

  /**
   * Check if navigation is allowed based on ECS configuration
   * @private
   */
  private static canNavigate(options?: NavigationOptions): boolean {
    // If no options, allow navigation
    if (!options) {
      return true;
    }

    // Check kill switch
    if (options.checkKillSwitch) {
      const killSwitch = getFeatureFlag('killSwitch', false);
      if (killSwitch) {
        warnDev('Navigation blocked: Kill switch is enabled');
        return false;
      }
    }

    // Check specific feature flags
    if (options.checkFeatureFlags && options.checkFeatureFlags.length > 0) {
      for (const flagName of options.checkFeatureFlags) {
        const flagValue = getFeatureFlag(flagName, false);
        if (!flagValue) {
          warnDev(`Navigation blocked: Feature flag '${flagName}' is disabled`);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Navigate with conditional logic based on device and policy results
   * Useful for post-authentication flows
   */
  static navigateAfterPolicyEvaluation(
    navigate: NavigateFunction,
    device: Device,
    mamRequired: boolean,
    context: NavigationContext,
    options?: NavigationOptions
  ): boolean {
    const route = device === Device.ANDROID && mamRequired 
      ? ROUTES.COMPANY_PORTAL_INSTALL 
      : ROUTES.APP_DOWNLOAD;
    return this.navigate(navigate, route, context, options);
  }
}

/**
 * Hook for using navigation service in components
 * Usage: const navigationService = useNavigationService();
 */
export function useNavigationService() {
  return NavigationService;
}